import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { emit } from '../../platform/analytics/analytics'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { Button } from '../../platform/primitives/Button'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { InstitutionAvatar } from '../../platform/primitives/InstitutionAvatar'
import { ListRow } from '../../platform/primitives/ListRow'
import { Panel } from '../../platform/primitives/Panel'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { useToast } from '../../platform/feedback/ToastProvider'
import '../../platform/notes.css'
import { REGIONS } from './region'
import { reconnectOffersOf, relinkIdOf, type InstitutionGroup } from './connectionGroups'
import { useAccounts, useAddAccountGate } from './useAccounts'
import { useLinkFlow, useProviderItems, useProviders, type ProviderOption } from './useAggregator'
import { useReconnect } from './useRelinkOutcome'
import type { ProviderType } from './providers/types'
import { ManualAccountForm } from './ManualAccountForm'
import './accounts.css'

/** M5-R1 — appears wherever linking is offered, not just on `/accounts`. */
const READ_ONLY_NOTE =
  'Read-only bank links — TrackE5 can see balances and transactions and can never move money.'

/** M5-R2 — the product states what it cannot do, spelled out rather than left
 *  implied by "read-only" alone. */
const CANNOT_DO_NOTE =
  'TrackE5 can never initiate a payment, move money, or change your bank connections.'

/** What the user has chosen. `offer` is not a state: it is derived from
 *  `choose` plus a pressed "Link a bank" (see `Connect`), so no effect has to
 *  move the screen into it. */
type Mode = 'choose' | 'link' | 'manual'
type View = Mode | 'offer'

const SKELETON = (
  <>
    <Skeleton height={44} />
    <Skeleton height={44} />
  </>
)

/**
 * The response's own `is_default` decides the pre-selection, never the first
 * entry by position — that would silently follow a routing-order change the
 * backend makes for its own reasons. Derived during render rather than
 * synced into state with an effect, so the Continue button below is never
 * disabled by a selection that visually exists but was never written back —
 * the bug this shape replaced: a `SegmentedControl` defaulting its OWN
 * display without telling its parent left `provider` state `null` until the
 * user happened to click the already-checked option.
 */
function effectiveProvider(providers: ProviderOption[], picked: ProviderType | null): ProviderType | null {
  return picked ?? providers.find((p) => p.isDefault)?.provider ?? providers[0]?.provider ?? null
}

/**
 * The picker — `SegmentedControl` (a real radiogroup), never a native
 * `<select>`, matching every other one-of-N choice in this app.
 *
 * **Renders exactly what the catalogue returns.** `display_name`/`subtitle`
 * are the response's own strings; nothing here hardcodes a provider's label,
 * which is the property the guard test in `providers/registry.test.ts`
 * polices from the other direction (no provider NAME hardcoded) — this is
 * its display-string twin.
 *
 * **A one-entry catalogue still renders as this same control**, not an
 * auto-advance — `SegmentedControl` with a single option is still a real
 * radiogroup a screen reader announces as "1 of 1", not a skipped step.
 */
function ProviderPicker({
  providers,
  value,
  onChange,
}: {
  providers: ProviderOption[]
  value: ProviderType | null
  onChange: (provider: ProviderType) => void
}) {
  const options = providers.map((p) => ({
    value: p.provider,
    label: p.subtitle ? `${p.displayName} — ${p.subtitle}` : p.displayName,
  }))

  return (
    <SegmentedControl
      label="Connection method"
      name="connect-provider"
      options={options}
      value={value ?? ''}
      onChange={(next) => onChange(next as ProviderType)}
    />
  )
}

/** Shared by each live button and its `ButtonPreview` in the locked gate, so a
 *  change to one cannot leave the other stale (#578). */
const LINK_VARIANT = 'primary'
export const LINK_LABEL = 'Link a bank'
const MANUAL_VARIANT = 'outline'
export const MANUAL_LABEL = 'Add manually'
const CHOOSE_SIZE = 'page'

/**
 * `/accounts/connect` — Ticket 3. Two branches (M5 spec's own "Add an
 * account" flow): **Link a bank** and **Add manually**, both gated by the
 * SAME `isOverFreeCap` (M5-R31/D-M5-6) — architect review, #397 (B4). An
 * earlier version left "Add manually" un-gated on a docblock's claim that
 * nobody under the cap reaches this screen unentitled, because `Accounts
 * .tsx`'s own button is gated first — that claim was checkably false:
 * `/accounts/connect` is a real, direct route (`ACCOUNT_CONNECT_PATH`,
 * `App.tsx`), reachable by URL or the back button regardless of what any
 * OTHER screen does. Gates live at the point of intent, which moved to this
 * screen when account creation did.
 *
 * **The entitlement check fires before any request leaves this screen**
 * (M5-R12) — `PremiumGate` structurally cannot render its real `children`
 * (the buttons that call `linkFlow.start` or open `ManualAccountForm`) while
 * locked, so an unentitled click never reaches `useLinkFlow` at all, let
 * alone `POST /aggregator/link-session`.
 *
 * **Closed by default while `useAccounts()` is unresolved** (architect
 * review, #397 B2) — `gated` reads `true` for an unentitled user on
 * `loading`/`error`/`empty`, not just on a resolved account list past the
 * cap. The earlier version read the RESOLVED `accounts` array for every
 * status, which defaulted to `[]` while still loading — an unentitled user
 * over the cap could click through in that window before the read settled,
 * minting a link token M5-R12 exists to prevent. `useEntitlement.tsx`'s own
 * docblock names the direction this has to fail in: "closed by default…
 * open-by-default… fails silently in the direction that gives away paid
 * features."
 *
 * **Not routed through `<Async>`.** `accounts` is a `synced` surface and
 * rule 10 asks for the five-state treatment, but `<Async>`'s `empty` branch
 * has a fixed shape (a title, an optional body, one action) that cannot
 * render this screen's two gated buttons — and empty is not an absence here,
 * it is the NORMAL first-time state this whole screen exists to serve. So
 * gating is `useAddAccountGate()` (`useAccounts.ts`, shared with Reconnect):
 * unresolved reads as closed, `empty`/`success`/`stale` read `isOverFreeCap`
 * against whatever accounts exist (none, for `empty`).
 *
 * **Reconnect first.** Pressing "Link a bank" while a disconnected bank is
 * reconnectable shows an offer to RECONNECT it before the provider picker — a
 * reconnect takes the old accounts and their history over, where an ordinary
 * link duplicates them. It waits (at most) for the accounts and the items to
 * resolve, and **an error from either goes straight to the ordinary link**:
 * the offer is a convenience, not a gate, so failing open costs nothing the
 * user needs. It sits behind the same `PremiumGate` as the button that opens
 * it, so it never delays the entitlement check.
 */
export function Connect() {
  const navigate = useNavigate()
  const toast = useToast()
  const gated = useAddAccountGate()

  const [mode, setMode] = useState<Mode>('choose')
  // "Link a bank" was pressed and the offer question is not yet answered.
  const [linkRequested, setLinkRequested] = useState(false)
  const accountsState = useAccounts()
  const itemsState = useProviderItems()
  const accountList = dataOf(accountsState)
  const itemList = dataOf(itemsState)
  const checking = accountsState.status === 'loading' || itemsState.status === 'loading'
  const offers: InstitutionGroup[] =
    accountList !== null && itemList !== null ? reconnectOffersOf(accountList, itemList) : []
  let view: View = mode
  if (mode === 'choose' && linkRequested && !checking) view = offers.length > 0 ? 'offer' : 'link'
  // `null` — no guess — until the user makes an explicit pick.
  // `GET /aggregator/providers`'s `region` is now optional and resolves the
  // same way everything else does when omitted (linked-account currency,
  // device locale, a default) — exactly the answer a user with no linked
  // accounts, and therefore no `users.country_code`, needs. Sending a
  // device-locale guess here would silently outrank that resolution, which
  // is worse than sending nothing. `region.ts` carries no client-side
  // default (N5, architect review) — the selector's displayed default is
  // `catalog.region`, the server's own answer, below.
  const [region, setRegion] = useState<string | null>(null)
  const [provider, setProvider] = useState<ProviderType | null>(null)
  const providersState = useProviders(region)
  const linkFlow = useLinkFlow()
  // Nothing left to offer once a reconnect is under way: on to Connections,
  // where the connection shows.
  const toConnections = useCallback(() => navigate('/connections'), [navigate])
  const reconnect = useReconnect('link_prompt', toConnections)

  useEffect(() => {
    if (linkFlow.state.status === 'success') {
      toast.show({ kind: 'success', message: 'Account connected.' })
      navigate('/accounts')
    }
  }, [linkFlow.state.status, toast, navigate])

  if (view === 'manual') {
    return <ManualAccountForm onClose={() => navigate('/accounts')} />
  }

  const linkButton = (
    <Button variant={LINK_VARIANT} size={CHOOSE_SIZE} disabled={linkRequested && checking} onClick={() => setLinkRequested(true)}>
      {linkRequested && checking ? 'Checking…' : LINK_LABEL}
    </Button>
  )
  const manualButton = (
    <Button variant={MANUAL_VARIANT} size={CHOOSE_SIZE} onClick={() => setMode('manual')}>
      {MANUAL_LABEL}
    </Button>
  )
  // One PremiumGate for both — B4 (architect review, #397): a locked user is
  // blocked from EITHER path to a new account, not just the one that used to
  // be gated. A single overlay covering both buttons matches "the second
  // attempt is what surfaces the paywall" (M5-R31) better than two separate
  // gates would, since both attempts are the same fact under isOverFreeCap.
  // The group is a flex column with the live Panel's gap, so the previews are
  // stretched to the column width the way the Panel stretches the live buttons.
  // A bare fragment would sit in the gate's block wrapper and shrink each span to
  // its label, with no gap between (#586).
  const choosePreview = (
    <div className="accounts-stack-group">
      <ButtonPreview variant={LINK_VARIANT} size={CHOOSE_SIZE}>
        {LINK_LABEL}
      </ButtonPreview>
      <ButtonPreview variant={MANUAL_VARIANT} size={CHOOSE_SIZE}>
        {MANUAL_LABEL}
      </ButtonPreview>
    </div>
  )
  const chooseLive = (
    <>
      {linkButton}
      {manualButton}
    </>
  )

  return (
    <>
      <h2 className="accounts-total__label">Add an account</h2>
      {/* `.note`'s `--ink2` measures against `--surface`, not the bare page
          background — `compositeContrast.test.tsx`'s route sweep found this
          composited at 3.45:1 in dark when these sat directly on the page
          (`/accounts/connect` is the first route to render `.note` text
          unconditionally, outside a data-dependent branch). A `Panel` gives
          it the SAME deterministic `--surface`/`--line` backdrop
          `PremiumGate`'s own docblock documents for the identical reason. */}
      <Panel className="accounts-stack accounts-stack--tight">
        <p className="note">{READ_ONLY_NOTE}</p>
        <p className="note">{CANNOT_DO_NOTE}</p>
      </Panel>

      {view === 'choose' && (
        <Panel className="accounts-stack">
          {gated ? (
            <PremiumGate entitlement="unlimited_accounts" preview={choosePreview} label="adding another account">
              {chooseLive}
            </PremiumGate>
          ) : (
            chooseLive
          )}
        </Panel>
      )}

      {view === 'offer' && (
        <ReconnectOffer
          offers={offers}
          pendingId={reconnect.pendingId}
          onReconnect={reconnect.start}
          onOtherBank={() => setMode('link')}
        />
      )}
      {reconnect.dialogs}

      {view === 'link' && (
        <Panel className="accounts-stack">
          <Async
            surface="aggregator-providers"
            state={providersState}
            skeleton={SKELETON}
            empty={{
              title: 'No connection methods are available in this region yet.',
              body: 'You can still add the account manually.',
              action: { label: 'Add manually', onAct: () => setMode('manual') },
            }}
          >
            {(catalog) => {
              // The Region control's displayed value is the SERVER's answer
              // once it has one — `region` state (the user's own explicit
              // pick) if set, `catalog.region` (what the server actually
              // resolved) otherwise. Never a client-side guess rendered
              // ahead of either.
              const displayedRegion = region ?? catalog.region
              const picked = effectiveProvider(catalog.providers, provider)
              return (
                <>
                  <SegmentedControl
                    label="Region"
                    name="connect-region"
                    options={REGIONS}
                    value={displayedRegion}
                    onChange={setRegion}
                  />

                  <ProviderPicker providers={catalog.providers} value={picked} onChange={setProvider} />

                  {linkFlow.state.status === 'manual_fallback' && (
                    <div>
                      {/* M5-R11: a failed link session offers manual entry
                          rather than dead-ending. */}
                      <p role="alert" className="note note--stale">
                        {linkFlow.state.message}
                      </p>
                      <Button variant="outline" size="page" onClick={() => setMode('manual')}>
                        Add a manual account instead
                      </Button>
                    </div>
                  )}

                  <Button
                    variant="primary"
                    size="page"
                    disabled={picked === null || linkFlow.state.status === 'pending'}
                    onClick={() => {
                      // The REQUEST still sends only an explicit pick —
                      // `region` state, not `displayedRegion` — so a link
                      // session minted before the user touched the control
                      // lets the server resolve it fresh rather than
                      // re-sending back its own earlier answer as though it
                      // were a choice.
                      if (picked !== null) void linkFlow.start(region, picked)
                    }}
                  >
                    {linkFlow.state.status === 'pending' ? 'Connecting…' : 'Continue'}
                  </Button>
                </>
              )
            }}
          </Async>

          <InlineLink to="/accounts">Cancel</InlineLink>
        </Panel>
      )}
    </>
  )
}

/** `data` of a settled state, `null` while loading or on an error. `empty` is a
 *  real answer — a list with nothing in it. */
function dataOf<T>(state: AsyncState<T[]>): T[] | null {
  switch (state.status) {
    case 'success':
    case 'stale':
      return state.data
    case 'empty':
      return []
    case 'loading':
    case 'error':
      return null
  }
}

/**
 * "Reconnect <bank>?" — the inline step before the provider picker.
 *
 * One bank: its name is the question, **Reconnect** is the page's one `--accent`
 * fill and "Link a different bank" the outline beside it. Several: a row each
 * with a Reconnect chip, no fill — the user is choosing, and rule 9 spends no
 * accent on a choice among equals.
 */
function ReconnectOffer({
  offers,
  pendingId,
  onReconnect,
  onOtherBank,
}: {
  offers: InstitutionGroup[]
  pendingId: string | null
  onReconnect: (group: InstitutionGroup) => void
  onOtherBank: () => void
}) {
  const only = offers.length === 1 ? offers[0] : undefined
  const busy = pendingId !== null
  // Why the other controls are off while one reconnect runs: stated as visible text
  // and pointed at by each disabled control (CLAUDE.md: every disabled control
  // states its reason). The pressed control reads "Reconnecting…" and needs none.
  const busyReasonId = useId()
  const busyReason = busy ? busyReasonId : undefined
  // Once per mount of the offer: a count of banks, never which ones. The ref survives
  // StrictMode's second effect pass.
  const shown = useRef(false)
  useEffect(() => {
    if (shown.current) return
    shown.current = true
    emit('reconnect_prompt_shown', { item_count: offers.length })
  }, [offers.length])
  return (
    <Panel className="accounts-stack">
      <h3 className="accounts-total__label">
        {only ? `Reconnect ${only.name}?` : 'Reconnect a bank you had before?'}
      </h3>
      <p className="note">
        Reconnecting signs in again and carries your accounts and their history over — nothing is duplicated.
      </p>
      <div role="list" aria-label="Banks you can reconnect" className="reconnect-offers">
        {offers.map((group) => (
          <div role="listitem" aria-label={group.name} key={group.key}>
            <ListRow
              leading={<InstitutionAvatar name={group.name} logoUrl={group.logoUrl} />}
              label={group.name}
              sublabel={group.accounts.map((a) => a.name).join(' · ')}
              trailing={
                only ? undefined : (
                  <span className="reconnect-offers__action">
                  <Button
                    variant="chip"
                    disabled={busy}
                    aria-busy={pendingId === relinkIdOf(group) ? true : undefined}
                    className={pendingId === relinkIdOf(group) ? 'reconnect-busy' : undefined}
                    aria-describedby={pendingId !== relinkIdOf(group) ? busyReason : undefined}
                    onClick={() => onReconnect(group)}
                  >
                    {pendingId !== null && pendingId === relinkIdOf(group) ? 'Reconnecting…' : 'Reconnect'}
                  </Button>
                  </span>
                )
              }
            />
          </div>
        ))}
      </div>
      {only && (
        <Button variant="primary" size="page" disabled={busy} aria-busy={busy ? true : undefined} className={busy ? 'reconnect-busy' : undefined} onClick={() => onReconnect(only)}>
          {busy ? 'Reconnecting…' : 'Reconnect'}
        </Button>
      )}
      {busy && (
        <p id={busyReasonId} role="note" className="note">
          A reconnect is in progress. The other choices are unavailable until it finishes.
        </p>
      )}
      <Button
        variant="outline"
        size="page"
        disabled={busy}
        aria-describedby={busyReason}
        onClick={() => {
          emit('reconnect_prompt_declined', { item_count: offers.length })
          onOtherBank()
        }}
      >
        Link a different bank
      </Button>
      {/* The link step's way out, here too: a way back that is not a choice about banks. */}
      <InlineLink to="/accounts">Cancel</InlineLink>
    </Panel>
  )
}
